import { useCallback } from "react"
import {
  Block,
  ButtonIcon,
  Copy03,
  FlexGrid,
  FlexGridItem,
  HeadingXSmall,
  LabelSmall,
  Mats,
  ParagraphMedium,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  RootState,
  selectClaimedReferral,
  useDappSelector,
} from "#/redux-state"
import { useCodeCopiedNotifcation, useCopyValue } from "#/shared/hooks"

type MatsReferralCodeBoxProps = {
  code: string
  claimed: boolean
  claimerUsername?: string
}

export default function MatsInvitesCodeBox({
  code,
  claimed,
  claimerUsername,
}: MatsReferralCodeBoxProps) {
  const claimedReferral = useDappSelector((state: RootState) =>
    selectClaimedReferral(state, code),
  )

  const { showCodeCopiedNotification } = useCodeCopiedNotifcation()
  const [css, theme] = useStyletron()

  const { copy } = useCopyValue(code, "Copy address")

  const copyCodeHandler = useCallback(() => {
    copy()
    showCodeCopiedNotification()
  }, [copy, showCodeCopiedNotification])

  return (
    <Block
      className={css({
        padding: theme.sizing.scale600,
        background: claimed ? theme.colors.green50 : theme.colors.white,
        borderColor: claimed ? theme.colors.green200 : theme.colors.white,
        borderWidth: "1px",
        borderRadius: theme.borders.radius400,
        height: "100%",
      })}
    >
      <FlexGrid
        flexGridColumnCount={2}
        flexGridColumnGap={theme.sizing.scale600}
        alignItems="center"
      >
        <FlexGridItem>
          <ParagraphMedium marginBottom={theme.sizing.scale100}>
            {claimed ? code : "Invite code"}
          </ParagraphMedium>
          <HeadingXSmall marginBottom={theme.sizing.scale600}>
            {claimed ? (
              <Block
                display="flex"
                alignItems="center"
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: theme.sizing.scale100,
                })}
              >
                <HeadingXSmall
                  as="span"
                  display="inline-block"
                  color={theme.colors.green600}
                >
                  {claimedReferral && claimedReferral.dailyMats > 0
                    ? `+${claimedReferral?.dailyMats}`
                    : claimedReferral?.dailyMats}
                </HeadingXSmall>{" "}
                <Mats size={16} />
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  per day
                </ParagraphSmall>
              </Block>
            ) : (
              code
            )}
          </HeadingXSmall>
          <Block minHeight="20px">
            {claimed && (
              <ParagraphSmall color={theme.colors.contentSecondary}>
                Claimed by{" "}
                <LabelSmall
                  as="span"
                  display="inline-block"
                  color={theme.colors.contentPrimary}
                >
                  {claimerUsername}.mezo
                </LabelSmall>
              </ParagraphSmall>
            )}
          </Block>
        </FlexGridItem>
        <FlexGridItem
          overrides={{
            Block: {
              style: () => ({
                flexGrow: 0,
                width: "auto",
              }),
            },
          }}
        >
          {!claimed && (
            <ButtonIcon shape="circle" size="small" onClick={copyCodeHandler}>
              <Copy03 size={16} color={theme.colors.black} />
            </ButtonIcon>
          )}
        </FlexGridItem>
      </FlexGrid>
    </Block>
  )
}
